import { checkReportRange, typesOfGroups, type EventRow, type ReportResult } from "@fts/core";
import { EmptyState, PageHeader, Panel } from "../../components/ui";
import { useI18n } from "../../i18n";
import { alarmTypeLabel } from "../../lib/alarms";
import { formatDateTimeIn } from "../../lib/export/sheet";
import { eventsSheet } from "../../lib/report-sheets";
import type { Route } from "../../router/hash";
import { useSession, type TenantData } from "../../state/session";
import { coverageOf } from "../../lib/report-figures";
import { ChartPanel, CoverageNote, PrintBrand, ReportContext, ReportEmpty, SummaryCards, periodText, previousOf } from "./ReportParts";
import { eventChart, eventSummary, toSummaryLines } from "./presenters";
import { ExportBar, FilterBar, Loading, contextChips, filterSummary, parseFilters, rangeKey, scopeVehicles, siteLabel, todayLocal, useLatest, useLoad, settleWithin, PREVIOUS_WAIT_MS, type ExportSpec } from "./shared";

/** Olay raporu (ADR 0018): alarms of the scope with type, time, vehicle, operator, site, severity, acknowledgement. */
export function EventsReport({ data, route }: { data: TenantData; route: Route }) {
  const { t, lang, locale } = useI18n();
  const { provider, tenant } = useSession();
  const f = parseFilters(route, data);
  const issue = checkReportRange(f.from, f.to);
  const tenantId = tenant?.id;
  const types = typesOfGroups(f.groups);
  const query = (id: string, range = { from: f.from, to: f.to }) => ({ tenantId: id, siteId: f.siteId, vehicleIds: f.vehicleIds, operatorId: f.operatorId, ...range, types });
  const deps = [provider, tenantId, f.siteId, f.vehicleIds.join(","), f.operatorId, f.from, f.to, types.join(",")];
  const state = useLoad<ReportResult<EventRow>>(tenantId && !issue ? () => provider.reportEvents(query(tenantId)) : null, deps);
  // The previous equal period through the same report call (same filters, same RLS): the comparison only.
  const prevRange = previousOf(f);
  // Fired only once the main result is in, and not at all when it was cut (a truncated list cannot be
  // totalled honestly, so there is nothing to compare).
  const mainComplete = state.status === "ready" && !!state.data && !state.data.truncated;
  const prev = useLoad<ReportResult<EventRow>>(tenantId && !issue && mainComplete ? () => provider.reportEvents(query(tenantId, prevRange)) : null, [...deps, mainComplete, state.data]);
  const prevLatest = useLatest(prev);
  const rows = state.data?.rows ?? [];
  const tz = state.data?.tz ?? "Europe/Istanbul";
  const ctx = { t, locale, lang };
  const prevRows = mainComplete && prev.status === "ready" && prev.data && !prev.data.truncated ? prev.data.rows : null;
  const summary = eventSummary(ctx, rows, prevRows);
  const chart = eventChart(ctx, rows, f.from, f.to, tz);
  const coverage = coverageOf({ vehicles: scopeVehicles(data, f.siteId, f.vehicleIds), devices: data.devices, from: f.from, to: f.to, tz, today: todayLocal() });
  const filters = filterSummary(t, data, f, { groups: true });
  const withOperators = rows.some((r) => r.operatorName !== null);
  const spec: ExportSpec | null = state.status === "ready" && state.data && tenantId
    ? {
        report: "events",
        reportName: t("reports.events.title"),
        slug: t("reports.events.slug"),
        part: `${f.from}_${f.to}`,
        tz,
        filters,
        period: periodText(t, lang, f.from, f.to),
        rows: rows.length,
        load: async (format) => {
          const [r, p] = await Promise.all([provider.exportEvents(query(tenantId), format), mainComplete && format === "pdf" ? settleWithin(prevLatest.current.promise, PREVIOUS_WAIT_MS) : Promise.resolve(null)]);
          return {
            sheets: [eventsSheet(t, r.rows, r.rows.some((x) => x.operatorName !== null))],
            tz: r.tz,
            summary: toSummaryLines(ctx, eventSummary(ctx, r.rows, r.truncated || !p || p.truncated ? null : p.rows)),
            chart: { title: t("reports.events.chart.title"), spec: eventChart(ctx, r.rows, f.from, f.to, r.tz), note: t("reports.events.chart.note") },
          };
        },
      }
    : null;
  const rk = rangeKey(issue);

  return (
    <>
      <PrintBrand title={t("reports.events.title")} tz={state.data?.tz ?? null} />
      <PageHeader title={t("reports.events.title")} subtitle={t("reports.events.subtitle")} eyebrow={t("nav.group.reports")} />
      <FilterBar data={data} path="/reports/events" filters={f} groups />
      {rk && (
        <p className="form-error" role="alert">
          {t(rk)}
        </p>
      )}
      <ReportContext chips={contextChips(t, filters)} from={f.from} to={f.to} tz={state.data?.tz ?? null} sample={provider.mode === "demo"} />
      {state.status === "ready" && rows.length > 0 && <SummaryCards items={summary} busy={prev.status === "loading"} prevFrom={prevRange.from} prevTo={prevRange.to} />}
      {state.status === "ready" && <CoverageNote coverage={coverage} tz={tz} />}
      <ExportBar spec={spec} disabled={rows.length === 0} />
      {state.status === "error" && (
        <p className="form-error" role="alert">
          {state.error}
        </p>
      )}
      {state.data?.truncated && <p className="form-error">{t("reports.truncated", { max: rows.length })}</p>}
      {state.status === "ready" && rows.length > 0 && <ChartPanel title={t("reports.events.chart.title")} spec={chart} note={t("reports.events.chart.note")} />}
      <Panel flush meta={state.status === "ready" ? <span>{t("reports.rows", { count: rows.length })}</span> : undefined}>
        {state.status !== "ready" && state.status !== "error" ? (
          rk ? <EmptyState compact icon="info" title={t(rk)} /> : <Loading />
        ) : rows.length === 0 ? (
          state.status === "error" ? null : <ReportEmpty path="/reports/events" filters={f} siteName={siteLabel(t, data, f.siteId)} />
        ) : (
          <div className="table-wrap table-scroll">
            <table className="table report-table">
              <thead>
                <tr>
                  <th scope="col">{t("reports.col.time")}</th>
                  <th scope="col">{t("reports.col.type")}</th>
                  <th scope="col">{t("reports.col.vehicle")}</th>
                  <th scope="col" className="col-hide-sm">{t("reports.col.site")}</th>
                  {withOperators && <th scope="col" className="col-hide-md">{t("reports.col.operator")}</th>}
                  <th scope="col">{t("reports.col.status")}</th>
                  <th scope="col" className="col-hide-md">{t("reports.col.ackNote")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="nowrap">
                      <a className="row-link" href={`#/alarms/${encodeURIComponent(r.id)}`}>
                        {formatDateTimeIn(r.occurredAt, tz, lang)}
                      </a>
                    </td>
                    <td>
                      {alarmTypeLabel(t, r.type)}
                      {r.type === "impact" && <span className="muted"> · {t("reports.severity", { level: r.severity })}</span>}
                      {r.geofenceName && <span className="muted"> · {r.geofenceName}</span>}
                    </td>
                    <td className="mono">{r.fleetNo ?? t("common.empty")}</td>
                    <td className="col-hide-sm">{r.siteName ?? t("common.empty")}</td>
                    {withOperators && <td className="col-hide-md">{r.operatorName ?? t("common.empty")}</td>}
                    <td>
                      <span className={`badge tone-${r.status === "open" ? "crit" : "ok"}`}>{r.status === "open" ? t("reports.status.open") : t("reports.status.acknowledged")}</span>
                    </td>
                    <td className="col-hide-md">{r.ackNote ?? t("common.empty")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <p className="panel-footnote">{t("reports.events.rules")}</p>
    </>
  );
}
